import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import C from "./palette";

const DAY = 86400000;
const PX_PER_DAY = 6; // absolute mode: width grows with the time span instead of squeezing
const PALETTE = [C.green, C.blue, C.orange, C.pink, C.purple, "#00838f", C.amber, C.red, C.lightGreen, C.indigo, "#795548", "#546e7a"];
const fmtDate = (ms) => new Date(ms).toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
const fmtFull = (ms) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

const btn = {
  background: C.grid, border: `1px solid ${C.line}`, borderRadius: 4,
  color: C.text, padding: "3px 10px", fontSize: "0.8rem", cursor: "pointer",
};

// books: [{id,title,author,totalPages}] newest first; progress: [{bookId,date:'YYYY-MM-DD',maxPage,secPerPage}]
const BookProgress = ({ books, progress }) => {
  const [metric, setMetric] = useState("progress"); // 'progress' = % read, 'pace' = seconds per page that day
  const [mode, setMode] = useState("absolute"); // 'absolute' = calendar date, 'relative' = days since start
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set(books.map((b) => b.id)));

  const lines = useMemo(() => {
    const byBook = new Map();
    progress.forEach((p) => {
      if (!byBook.has(p.bookId)) byBook.set(p.bookId, []);
      byBook.get(p.bookId).push(p);
    });
    const out = new Map();
    books.forEach((b) => {
      const rows = (byBook.get(b.id) || []).slice().sort((a, c) => (a.date < c.date ? -1 : 1));
      if (!rows.length || !b.totalPages) return;
      const start = Date.parse(rows[0].date + "T00:00:00Z");
      let best = 0;
      out.set(b.id, rows.map((r) => {
        best = Math.max(best, Math.min(100, (r.maxPage / b.totalPages) * 100)); // progress never goes backwards
        const t = Date.parse(r.date + "T00:00:00Z");
        return { t, day: Math.round((t - start) / DAY), pct: best, pace: r.secPerPage };
      }));
    });
    return out;
  }, [books, progress]);

  const q = query.trim().toLowerCase();
  const shown = books.filter((b) => lines.has(b.id) && (!q || `${b.title} ${b.author}`.toLowerCase().includes(q)));
  const colorOf = (id) => PALETTE[books.findIndex((b) => b.id === id) % PALETTE.length];

  const setMany = (ids, on) => setSelected((s) => {
    const n = new Set(s);
    ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
    return n;
  });
  const toggle = (id) => setMany([id], !selected.has(id));
  const pickLast = (n) => setSelected(new Set(books.filter((b) => lines.has(b.id)).slice(0, n).map((b) => b.id)));
  const invert = () => setSelected((s) => new Set(shown.filter((b) => !s.has(b.id)).map((b) => b.id)
    .concat([...s].filter((id) => !shown.some((b) => b.id === id)))));

  const abs = mode === "absolute";
  const pace = metric === "pace";
  const chosen = books.filter((b) => selected.has(b.id) && lines.has(b.id));

  // Scrollable width for absolute mode; null = fit the container.
  const spanDays = useMemo(() => {
    const ts = chosen.flatMap((b) => lines.get(b.id).map((p) => p.t));
    return ts.length ? Math.ceil((Math.max(...ts) - Math.min(...ts)) / DAY) : 0;
  }, [chosen, lines]);
  const scrollRef = useRef(null);
  const innerWidth = abs ? Math.max(spanDays * PX_PER_DAY + 90, 0) : 0; // +90 for the y axis
  useEffect(() => { // open at the latest dates
    const el = scrollRef.current;
    if (abs && el) el.scrollLeft = el.scrollWidth;
  }, [abs, innerWidth]);
  const data = {
    datasets: chosen.map((b) => ({
      label: b.title,
      data: lines.get(b.id).map((p) => ({ x: abs ? p.t : p.day, y: pace ? p.pace : p.pct })),
      borderColor: colorOf(b.id),
      backgroundColor: colorOf(b.id),
      borderWidth: 2,
      pointRadius: 2,
      tension: 0,
    })),
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: { display: false }, // the checklist is the legend
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.dataset.label,
          label: (c) => `${abs ? fmtFull(c.parsed.x) : `day ${c.parsed.x}`} · ${c.parsed.y.toFixed(1)}${pace ? " s/page" : "%"}`,
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        title: { display: true, text: abs ? "Date" : "Days since starting the book", color: C.text },
        ticks: { color: C.text, callback: (v) => (abs ? fmtDate(v) : v), maxTicksLimit: 10 },
        grid: { color: C.grid },
      },
      y: {
        min: 0, ...(pace ? {} : { max: 100 }),
        title: { display: true, text: pace ? "Seconds per page" : "Progress (%)", color: C.text },
        ticks: { color: C.text, callback: (v) => v + (pace ? "s" : "%") },
        grid: { color: C.grid },
      },
    },
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="chart-card">
          <Card.Body>
            <Card.Title>{pace ? "Reading Pace Over Time (per book)" : "Book Progress Over Time"}</Card.Title>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }} role="group" aria-label="Metric">
              <span style={{ color: C.muted, fontSize: "0.85rem" }}>Show:</span>
              {[["progress", "Progress (%)"], ["pace", "Pace (sec/page)"]].map(([id, label]) => (
                <button key={id} type="button" aria-pressed={metric === id} onClick={() => setMetric(id)}
                        style={{ ...btn, ...(metric === id ? { background: C.activeBg, borderColor: C.activeBg, color: C.activeFg } : {}) }}>
                  {label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }} role="group" aria-label="X axis">
              <span style={{ color: C.muted, fontSize: "0.85rem" }}>X axis:</span>
              {[["relative", "Relative (days since start)"], ["absolute", "Absolute (calendar date)"]].map(([id, label]) => (
                <button key={id} type="button" aria-pressed={mode === id} onClick={() => setMode(id)}
                        style={{ ...btn, ...(mode === id ? { background: C.activeBg, borderColor: C.activeBg, color: C.activeFg } : {}) }}>
                  {label}
                </button>
              ))}
            </div>

            {chosen.length ? (
              <>
              <div ref={scrollRef} style={{ overflowX: abs ? "auto" : "visible" }}>
                <div className="chart-container" style={abs ? { minWidth: innerWidth } : undefined}>
                  <Line data={data} options={options} />
                </div>
              </div>
              {abs && innerWidth > 0 && <div style={{ color: C.dim, fontSize: "0.75rem", marginTop: 4 }}>Scroll sideways for earlier dates · {spanDays} days shown</div>}
              </>
            ) : (
              <div style={{ padding: "3rem", textAlign: "center", color: C.muted, border: `1px dashed ${C.line}`, borderRadius: 8 }}>
                Pick one or more books below.
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="search title or author"
                       aria-label="Search books" style={{ flex: "1 1 180px", minWidth: 0, padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.line}`, background: C.panel2, color: C.text }} />
                <button type="button" style={btn} onClick={() => setMany(shown.map((b) => b.id), true)}>Select all{q ? " matching" : ""}</button>
                <button type="button" style={btn} onClick={() => setMany(shown.map((b) => b.id), false)}>Clear{q ? " matching" : ""}</button>
                <button type="button" style={btn} onClick={invert}>Invert</button>
                <button type="button" style={btn} onClick={() => pickLast(5)}>Last 5</button>
                <button type="button" style={btn} onClick={() => pickLast(10)}>Last 10</button>
              </div>
              <div style={{ color: C.muted, fontSize: "0.8rem", marginBottom: 6 }}>
                {chosen.length} of {lines.size} books shown
              </div>
              <div style={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${C.grid}`, borderRadius: 6, padding: 6 }}>
                {shown.map((b) => (
                  <label key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 4px", cursor: "pointer", color: C.text, fontSize: "0.85rem" }}>
                    <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} />
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: colorOf(b.id), flex: "none", opacity: selected.has(b.id) ? 1 : 0.35 }} />
                    <span>{b.title} <span style={{ color: C.dim }}>— {b.author}</span></span>
                  </label>
                ))}
                {!shown.length && <div style={{ color: C.muted, padding: 8 }}>No books match.</div>}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default BookProgress;
